import { Component, OnInit, ViewChild } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import {
  DispatchTripGridItem,
  DispatchTripFilter,
  DispatchTripStatus,
  DispatchTripStatusLabels,
  DispatcherUser,
  DispatchTrip,
} from "@app/shared/models/DispatchTrip.model";
import { DispatchTripService } from "../shared/dispatch-trip.service";
import { clientService } from "@app/client/shared/client.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { forkJoin, Observable, map } from "rxjs";
import { LgtGridStaticComponent } from "@app/shared/components/lgt-grid-static/lgt-grid-static.component";
import {
  EmailPreviewItem,
  EmailPreviewModalComponent,
} from "../email-preview-modal/email-preview-modal.component";
import { ConfigService } from "@app/utilities/config/shared/config.service";

@Component({
  selector: "app-dispatch-trips",
  templateUrl: "./dispatch-trips.component.html",
  styleUrls: ["./dispatch-trips.component.css"],
})
export class DispatchTripsComponent implements OnInit {
  @ViewChild(LgtGridStaticComponent, { static: false }) gridComponent: LgtGridStaticComponent;

  trips: DispatchTripGridItem[] = [];
  isLoading = true;

  // Modal state
  showModal = false;
  selectedTripId: string | null = null;

  // Email preview modal
  showEmailPreviewModal = false;
  emailPreviewItems: EmailPreviewItem[] = [];
  pendingEmailTripIds: string[] = [];

  // Driver assignment (inline in grid)
  driverNotesCcEmail: string = '';
  driverList: any[] = [];
  assignmentDrivers: any[] = [];
  driverCardDataCache: { [id: string]: any } = {};

  // Filter state
  filter: DispatchTripFilter = {};
  dispatchers: DispatcherUser[] = [];
  billToClients: any[] = [];
  drivers: any[] = [];
  statusOptions = [
    { label: "All", value: null },
    { label: "Requested", value: DispatchTripStatus.Requested },
    { label: "Pending", value: DispatchTripStatus.Pending },
    { label: "In Transit", value: DispatchTripStatus.InTransit },
    { label: "Delivered", value: DispatchTripStatus.Delivered },
  ];

  // Filters for lgt-grid-static
  filters = {
    referenceNumber: "",
    startDate: "",
    clientName: "",
    billToName: "",
    bolNumber: "",
    loadLocationName: "",
    unloadLocationName: "",
    deliveryWindow: "",
    productName: "",
    driverName: "",
    statusDisplay: "",
    dispatcherName: "",
  };

  // Button actions for lgt-grid-static
  newButtonAction: any = this.openCreateModal.bind(this);
  newButtonOptions: any = [
    {
      label: "Create Trip",
      command: () => {
        this.openCreateModal();
      },
    },
  ];
  viewDetail = this.onRowDblClick.bind(this);
  loadData = this.loadTripsForGrid.bind(this);

  // Grid columns
  cols = [
    { field: "driverName", header: "Driver", type: "driverAssignment" },
    { field: "referenceNumber", header: "Reference #" },
    { field: "startDate", header: "Start Date", type: "date" },
    { field: "clientName", header: "Client" },
    { field: "billToName", header: "Bill To" },
    { field: "bolNumber", header: "BOL" },
    { field: "loadLocationName", header: "Load Location" },
    { field: "unloadLocationName", header: "Unload Location" },
    { field: "deliveryWindow", header: "Delivery Window" },
    { field: "productName", header: "Product" },
    { field: "statusDisplay", header: "Status" },
    { field: "dispatcherName", header: "Dispatcher" },
  ];

  constructor(
    private dispatchTripService: DispatchTripService,
    private clientService: clientService,
    private driverService: DriverService,
    private toastr: ToastrService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.loadFilterOptions();
    this.configService.getDriverNotesCcEmail().subscribe(({ ccEmail }) => {
      this.driverNotesCcEmail = ccEmail;
    });
  }

  loadFilterOptions(): void {
    forkJoin({
      dispatchers: this.dispatchTripService.getDispatchers(),
      clients: this.clientService.getClientList(),
      drivers: this.driverService.getDriverList(false),
    }).subscribe({
      next: (data) => {
        this.dispatchers = data.dispatchers;
        this.billToClients = data.clients.filter((c: any) => c.clientId); // Bill-to clients only
        this.drivers = data.drivers;
        this.driverList = data.drivers;
        this.assignmentDrivers = data.drivers.map((d: any) => ({
          ...d,
          displayName: d.lastName && d.name
            ? `${d.lastName}, ${d.name}`
            : d.name || d.lastName || "Unknown",
        }));
        // Build driver card data cache from available list data
        data.drivers.forEach((d: any) => {
          this.driverCardDataCache[d.id] = {
            name: d.name || "",
            lastName: d.lastName || "",
            driverNo: d.driverNo || null,
            picture: null,
            specialNote: null,
            cellphone: d.cellphone || null,
            currentLocation: null,
            autoTransmissionOnly: false,
            boardName: d.boardName || null,
            billToClientName: d.billToClientName || null,
            peopleNetId: d.peopleNetId || null,
            tractorNumber: d.tractors?.length ? d.tractors[0].unitNumber : null,
            trailerNumber: null,
            homeLocation: null,
            teamMembers: null,
          };
        });
      },
      error: (error) => {
        console.error("Failed to load filter options", error);
      },
    });
  }

  loadTrips(): void {
    this.isLoading = true;
    this.dispatchTripService.getAll(this.filter).subscribe({
      next: (trips) => {
        this.trips = trips.map((t) => ({
          ...t,
          startDate: t.startDate ? new Date(t.startDate) : null,
        }));
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Failed to load trips");
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  loadTripsForGrid(): Observable<any[]> {
    return this.dispatchTripService.getAll(this.filter).pipe(
      map((trips) =>
        trips.map((t) => ({
          ...t,
          startDate: t.startDate ? new Date(t.startDate) : null,
        }))
      )
    );
  }

  onFilterChange(): void {
    if (this.gridComponent) {
      this.gridComponent.ngOnInit();
    }
  }

  clearFilters(): void {
    this.filter = {};
    if (this.gridComponent) {
      this.gridComponent.ngOnInit();
    }
  }

  onAssignDriverInline(event: { tripId: string; driverId: string | null }): void {
    this.dispatchTripService.updateDriver(event.tripId, event.driverId).subscribe({
      next: () => {
        this.toastr.success("Driver assigned successfully");
      },
      error: (error) => {
        this.toastr.error(error.error || "Failed to assign driver");
        // Reload grid data to revert the dropdown to previous state
        if (this.gridComponent) {
          this.gridComponent.ngOnInit();
        }
      },
    });
  }

  onEmailSelectedTrips(tripIds: string[]): void {
    if (!tripIds || tripIds.length === 0) return;

    // Use grid data since this.trips may not be populated (loaded separately via loadData)
    const trips = this.gridComponent?.data || this.trips;
    const selectedTrips = trips.filter((t: any) => tripIds.includes(t.id));

    // Build email preview items from the grid data
    this.emailPreviewItems = selectedTrips.map((t: any) =>
      EmailPreviewModalComponent.buildEmailPreview(t, this.driverNotesCcEmail)
    );
    this.pendingEmailTripIds = tripIds;
    this.showEmailPreviewModal = true;
  }

  onEmailPreviewConfirmed(): void {
    this.showEmailPreviewModal = false;

    this.dispatchTripService.sendBatchEmails(this.pendingEmailTripIds).subscribe({
      next: (result) => {
        this.toastr.success(
          `Emails sent: ${result.sentCount} succeeded, ${result.failCount} failed out of ${result.total}`
        );
        if (this.gridComponent) {
          this.gridComponent.clearAllSelected();
          this.gridComponent.ngOnInit();
        }
      },
      error: (error) => {
        this.toastr.error(error.error || "Failed to send emails");
      },
    });

    this.pendingEmailTripIds = [];
  }

  onEmailPreviewCancelled(): void {
    this.showEmailPreviewModal = false;
    this.pendingEmailTripIds = [];
  }

  openCreateModal(): void {
    this.selectedTripId = null;
    this.showModal = true;
  }

  onRowDblClick(trip: DispatchTripGridItem): void {
    this.selectedTripId = trip.id;
    this.showModal = true;
  }

  onModalSaved(trip: DispatchTrip): void {
    this.showModal = false;
    this.selectedTripId = null;
    this.loadTrips();
  }

  onModalCancelled(): void {
    this.showModal = false;
    this.selectedTripId = null;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case "Requested":
        return "badge-warning";
      case "Pending":
        return "badge-info";
      case "InTransit":
      case "In Transit":
        return "badge-primary";
      case "Delivered":
        return "badge-success";
      default:
        return "badge-secondary";
    }
  }
}
